import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { MealPlaceholderIcon } from "@/components/icons/meal-placeholder-icon";
import ImageWithLoading from "@/components/image/image-with-loading";
import { LikeMealButton } from "@/features/meal-plan/components/like-meal-button";
import { useGetFavouriteMeals } from "@/features/meal-plan/hooks/use-get-favourite-meals";
import { useMealDetailData } from "@/features/meal-plan/hooks/use-meal-detail-data";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonSkeletonText } from "@ionic/react";
import { memo, useMemo } from "react";

interface HomeFeedMealsListProps {
  title: string;
  meals: Meal[];
  isLoading: boolean;
  emptyStateText?: string;
}

const FALLBACK_MEAL_TIME = MealTime.SNACK;
const MEAL_TIME_ORDER: MealTime[] = [
  MealTime.BREAKFAST,
  MealTime.LUNCH,
  MealTime.DINNER,
  MealTime.SNACK,
];

const inferMealTimeFromMeal = (meal: Meal): MealTime => {
  const categoryCandidates = [
    meal.mealChoiceCategory,
    meal.mealTypeCategory,
    meal.mealTypeCategory2,
  ]
    .filter((value): value is string => Boolean(value))
    .map((value) => value.toLowerCase());

  if (categoryCandidates.some((value) => value.includes("breakfast"))) {
    return MealTime.BREAKFAST;
  }

  if (categoryCandidates.some((value) => value.includes("lunch"))) {
    return MealTime.LUNCH;
  }

  if (categoryCandidates.some((value) => value.includes("dinner") || value.includes("evening"))) {
    return MealTime.DINNER;
  }

  if (categoryCandidates.some((value) => value.includes("snack"))) {
    return MealTime.SNACK;
  }

  return FALLBACK_MEAL_TIME;
};

export const HomeFeedMealsList = memo(function HomeFeedMealsList({
  title,
  meals,
  isLoading,
  emptyStateText = "No meals available right now.",
}: HomeFeedMealsListProps) {
  const { onMealDetailClick } = useMealDetailData();
  const { favouriteMealsByMealTime, isFavouriteLoading } = useGetFavouriteMeals();

  const currentWeekday = useMemo(() => {
    return new Date().toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
  }, []);

  const favouriteMealTimeMap = useMemo(() => {
    const map = new Map<string, MealTime>();

    MEAL_TIME_ORDER.forEach((mealTime) => {
      const mealList = favouriteMealsByMealTime[mealTime] ?? [];
      mealList.forEach((meal) => {
        if (meal.id) {
          map.set(meal.id, mealTime);
        }
      });
    });

    return map;
  }, [favouriteMealsByMealTime]);

  const resolveMealTime = (meal: Meal): MealTime => {
    const mealId = meal.id ?? "";
    if (mealId && favouriteMealTimeMap.has(mealId)) {
      return favouriteMealTimeMap.get(mealId) ?? FALLBACK_MEAL_TIME;
    }
    return inferMealTimeFromMeal(meal);
  };

  const handleMealClick = (meal: Meal) => {
    onMealDetailClick({
      meal,
      mealTime: resolveMealTime(meal),
      mealDay: currentWeekday,
      mealIndex: 0,
    });
  };

  if (isLoading || isFavouriteLoading) {
    return (
      <div className="flex flex-col gap-[18px]">
        <h2 className={cn(textVariants.title3, "text-white")}>{title}</h2>
        <div className="no-scrollbar -mr-4 flex gap-3 overflow-x-auto pr-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <IonSkeletonText
              key={index}
              animated
              className="h-[230px] w-[230px] shrink-0 rounded-[20px]"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4.5 pb-5">
      <h3 className={cn(textVariants.title3, "text-white capitalize")}>{title}</h3>

      {meals.length === 0 ? (
        <div className="h-[230px] w-full">
          <p className={cn(textVariants.bodySmall, "text-white/70")}>{emptyStateText}</p>
        </div>
      ) : (
        <div className="no-scrollbar -mr-4 flex h-[230px] gap-3 overflow-x-auto pr-4">
          {meals.map((meal, index) => {
            const mealId = meal.id ?? "";

            return (
              <div
                key={mealId || `${meal.name}-${index}`}
                className="relative h-[230px] w-[230px] shrink-0 overflow-hidden rounded-[20px]"
              >
                <button
                  type="button"
                  onClick={() => handleMealClick(meal)}
                  className="h-full w-full"
                >
                  <ImageWithLoading
                    src={meal.imageUri}
                    alt={meal.name}
                    classNames={{
                      container: "h-full w-full bg-image-loading",
                      image: "h-full w-full object-cover",
                    }}
                    notFoundImage={
                      <div className="bg-image-loading flex h-full w-full items-center justify-center">
                        <MealPlaceholderIcon className="h-8 w-8 fill-gray-50" />
                      </div>
                    }
                    errorImage={
                      <div className="bg-image-loading flex h-full w-full items-center justify-center">
                        <MealPlaceholderIcon className="h-8 w-8 fill-gray-50" />
                      </div>
                    }
                  />
                </button>

                <div className="to-screen-gs-1/95 pointer-events-none absolute inset-x-0 bottom-0 z-10 h-[99px] bg-gradient-to-b from-transparent" />

                <p
                  className={cn(
                    textVariants.bodySingleLine,
                    "pointer-events-none absolute right-[18px] bottom-[18px] left-[18px] z-20 line-clamp-2 text-center text-white",
                  )}
                >
                  {meal.name}
                </p>

                <LikeMealButton
                  meal={meal}
                  mealTime={resolveMealTime(meal)}
                  filledColor="primary"
                  unfilledColor="primary"
                  className="absolute top-[10px] right-[10px] z-30 h-11 w-11 bg-white p-2.5"
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});
